/**
 * Authentication Context and Provider
 *
 * Manages user authentication state and provides auth methods
 * to child components via React Context.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useMsal } from '@azure/msal-react'
import { useQueryClient } from '@tanstack/react-query'
import { loginRequest, getRedirectResult, clearRedirectResult } from './authConfig'
import { authEvents } from './authEvents'
import apiClient from '@/core/api/client'
import { User } from '@/shared/types/common.types'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isLoggingOut: boolean
  deactivationError: string | null
  login: () => Promise<void>
  loginWithCredentials: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => Promise<string>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
}

const LOCAL_TOKEN_KEY = 'hrms_local_token'

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal()
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [deactivationError, setDeactivationError] = useState<string | null>(null)
  const [localToken, setLocalToken] = useState<string | null>(() =>
    sessionStorage.getItem(LOCAL_TOKEN_KEY)
  )
  const isDeactivatedRef = useRef(false)

  // Centralized logout function
  const performLogout = useCallback(async (silent = false) => {
    if (isLoggingOut) return // Prevent multiple logout attempts

    setIsLoggingOut(true)

    try {
      // Clear all React Query cache first to prevent data leakage
      queryClient.clear()

      // Clear local token if exists
      const hasLocalToken = sessionStorage.getItem(LOCAL_TOKEN_KEY)
      if (hasLocalToken) {
        sessionStorage.removeItem(LOCAL_TOKEN_KEY)
        setLocalToken(null)
        setUser(null)
        setIsLoggingOut(false)
        return
      }

      // Otherwise logout from Microsoft
      if (!silent && accounts.length > 0) {
        try {
          await instance.logoutPopup({
            postLogoutRedirectUri: window.location.origin,
          })
        } catch (error) {
          // If popup fails, try redirect logout
          console.error('Popup logout failed, trying redirect:', error)
          await instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin,
          })
        }
      }

      setUser(null)
    } catch (error) {
      console.error('Logout failed:', error)
      // Force clear user state even if logout API fails
      setUser(null)
      sessionStorage.removeItem(LOCAL_TOKEN_KEY)
      setLocalToken(null)
    } finally {
      setIsLoggingOut(false)
    }
  }, [instance, accounts, queryClient, isLoggingOut])

  // Listen for auth events from API client
  useEffect(() => {
    const unsubscribeExpired = authEvents.on('auth:sessionExpired', async () => {
      await performLogout(true)
    })

    const unsubscribeDeactivated = authEvents.on('auth:accountDeactivated', async (event) => {
      isDeactivatedRef.current = true
      const message = event.message || 'Your account has been deactivated. Please contact your administrator.'
      setDeactivationError(message)
      await performLogout(true)
    })

    return () => {
      unsubscribeExpired()
      unsubscribeDeactivated()
    }
  }, [performLogout])

  useEffect(() => {
    // Don't reinitialize if the account was explicitly deactivated this session
    if (isDeactivatedRef.current) {
      setIsLoading(false)
      return
    }

    // Check for local token first
    if (localToken) {
      initializeLocalUser()
    } else if (accounts.length > 0) {
      // User is logged in with Microsoft
      initializeUser()
    } else {
      // Check if we just returned from a redirect
      const redirectResult = getRedirectResult()
      if (redirectResult?.account) {
        // User just authenticated via redirect, initialize
        initializeUser()
        // Clear the redirect result after using it
        clearRedirectResult()
      } else {
        setIsLoading(false)
      }
    }
  }, [accounts, localToken])

  const initializeLocalUser = async () => {
    try {
      const response = await apiClient.get<{ user: User }>(
        '/v1/auth/me',
        {
          headers: {
            Authorization: `Bearer ${localToken}`,
          },
        }
      )
      setUser(response.data.user)
    } catch (error) {
      console.error('Failed to initialize local user:', error)
      // Clear invalid token
      sessionStorage.removeItem(LOCAL_TOKEN_KEY)
      setLocalToken(null)
    } finally {
      setIsLoading(false)
    }
  }

  const initializeUser = async () => {
    try {
      // Check if we have a token from redirect first
      const redirectResult = getRedirectResult()
      let token: string

      if (redirectResult?.idToken) {
        token = redirectResult.idToken
      } else {
        token = await getAccessToken()
      }

      // Send token to backend for validation and user provisioning
      const response = await apiClient.post<{ user: User }>(
        '/v1/auth/validate',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      setUser(response.data.user)
    } catch (error) {
      console.error('Failed to initialize user:', error)
      // Don't call logout here, just clear the state
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async () => {
    isDeactivatedRef.current = false
    setDeactivationError(null)
    setIsLoading(true)
    try {
      // Use popup for login - always use current origin for popup redirectUri
      await instance.loginPopup({
        ...loginRequest,
        redirectUri: window.location.origin,
      })
      // User will be initialized in useEffect when accounts change
    } catch (error) {
      // If popup is blocked or fails, fall back to redirect
      if (error instanceof Error &&
          (error.message.includes('popup') ||
           error.message.includes('user_cancelled') ||
           error.message.includes('interaction_in_progress'))) {
        console.log('Popup issue, falling back to redirect')
        await instance.loginRedirect(loginRequest)
      } else {
        console.error('Login failed:', error)
        setIsLoading(false)
        throw error
      }
    }
  }

  const logout = async () => {
    await performLogout(false)
  }

  const loginWithCredentials = async (username: string, password: string) => {
    setDeactivationError(null)
    setIsLoading(true)
    try {
      const response = await apiClient.post<{
        access_token: string
        token_type: string
        user: User
      }>('/v1/auth/login', { username, password })

      const { access_token, user: userData } = response.data

      // Store token in session storage
      sessionStorage.setItem(LOCAL_TOKEN_KEY, access_token)
      setLocalToken(access_token)
      setUser(userData)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const getAccessToken = async (): Promise<string> => {
    // Return local token if available
    if (localToken) {
      return localToken
    }

    const account = accounts[0]
    if (!account) {
      throw new Error('No account found')
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      })
      return response.idToken
    } catch (error) {
      console.error('Token acquisition failed:', error)
      // If silent token acquisition fails, try interactive
      const response = await instance.acquireTokenPopup({
        ...loginRequest,
        redirectUri: window.location.origin,
      })
      return response.idToken
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isLoggingOut,
    deactivationError,
    login,
    loginWithCredentials,
    logout,
    getAccessToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
